import { useMemo, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { confirmable } from 'react-confirm';
import { useForm, useWatch } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import i18n from 'src/locales/i18n';
import { useGetCommonCodeType } from 'src/api/common_code';
import { SelectCurrency } from 'src/components/hook_form/select_currency';
import FormProvider from 'src/components/hook_form/form_provider';
import RHFTextField from 'src/components/hook_form/rhf_text_field';
import StackedModal from './stacked_modal';

i18n.loadNamespaces(['return_inbound', 'common', 'placeholder', 'helper_text', 'validation']);

export const FEE_KEYS = ['codFee', 'returnFee', 'prepaidAmount'];

export const feeLabelKey = (key) => {
  if (key === 'codFee') return 'cod_fee';
  if (key === 'returnFee') return 'return_fee';
  return 'prepaid_amount';
};

export const toFeeNumber = (value, decimals = 0) => {
  if (value === '' || value === null || value === undefined) return null;
  const numeric = Number(`${value}`.replace(/,/g, ''));
  if (Number.isNaN(numeric)) return null;
  return decimals > 0 ? Number(numeric.toFixed(decimals)) : Math.trunc(numeric);
};

export const formatFee = (value, decimals = 0) =>
  value === null || value === undefined
    ? ''
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: decimals });

export const EMPTY_CURRENCY_META = { symbol: '', decimals: 0 };

export function useCurrencyMap() {
  const { searchResults } = useGetCommonCodeType({ codeType: 'currency_code', isActive: true });
  return useMemo(() => {
    const details = searchResults?.find((c) => c.codeType === 'currency_code')?.details ?? [];
    return Object.fromEntries(
      details.map((d) => [
        d.codeValue,
        { symbol: d.codeReference1 || d.codeValue, decimals: Number(d.codeReference4) || 0 },
      ]),
    );
  }, [searchResults]);
}

export function useCurrencyMeta(currencyCode) {
  const currencyMap = useCurrencyMap();
  return useMemo(
    () => (currencyCode ? currencyMap[currencyCode] ?? EMPTY_CURRENCY_META : EMPTY_CURRENCY_META),
    [currencyMap, currencyCode],
  );
}

export const feeModalSx = (theme) => ({
  '& .MuiInputBase-input': theme.typography.body_md,
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--app-icon-secondary)',
    WebkitTextFillColor: 'var(--app-icon-secondary)',
    opacity: 1,
  },
  '& .MuiInputAdornment-root .MuiTypography-root': {
    ...theme.typography.body_md,
    color: theme.palette.text.secondary,
  },
});

export const feeRow = (label, value) => (
  <Grid
    container
    alignItems="center"
    wrap="nowrap"
    spacing={1}
  >
    <Grid
      item
      xs={5}
    >
      <Typography variant="body_md_medium">{label}</Typography>
    </Grid>
    <Grid
      item
      xs={7}
      display="flex"
      justifyContent="flex-end"
      sx={{ minWidth: 0 }}
    >
      {typeof value === 'object' ? (
        value
      ) : (
        <Typography
          variant="body_md"
          sx={{ textAlign: 'right' }}
        >
          {value}
        </Typography>
      )}
    </Grid>
  </Grid>
);

const ReturnFeeDialog = ({ show, proceed, count }) => {
  const schema = useMemo(
    () =>
      Yup.object().shape({
        currencyCode: Yup.string().test(
          'currency-required',
          i18n.t('validation:currency_required'),
          function (value) {
            const hasFee = FEE_KEYS.some((key) => toFeeNumber(this.parent[key]) !== null);
            return !hasFee || Boolean(value);
          },
        ),
      }),
    [],
  );

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      codFee: '',
      returnFee: '',
      prepaidAmount: '',
      currencyCode: '',
    },
  });
  const [submitting, setSubmitting] = useState(false);

  const values = useWatch({ control: methods.control });
  const currencyMap = useCurrencyMap();
  const { symbol, decimals } = currencyMap[values.currencyCode] ?? EMPTY_CURRENCY_META;

  return (
    <StackedModal
      isOpen={show}
      title={i18n.t('return_inbound:fee.receive_title')}
      sx={{ width: { xs: '95vw', sm: '520px' }, maxWidth: '95vw' }}
      content={
        <FormProvider methods={methods}>
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              width: '100%',
              ...feeModalSx(theme),
            })}
          >
            {feeRow(
              i18n.t('return_inbound:fee.total_qty'),
              i18n.t('return_inbound:fee.products', { count: count ?? 0 }),
            )}

            <Box>
              {feeRow(
                i18n.t('return_inbound:fee.currency'),
                <Box sx={{ width: '100%' }}>
                  <SelectCurrency
                    value={values.currencyCode}
                    setValue={(value) => {
                      methods.setValue('currencyCode', value);
                      const nextDecimals = currencyMap[value]?.decimals ?? 0;
                      FEE_KEYS.forEach((key) => {
                        const current = methods.getValues(key);
                        if (current === '' || current === null || current === undefined) return;
                        methods.setValue(key, toFeeNumber(current, nextDecimals));
                      });
                    }}
                    placeholder={i18n.t('placeholder:currency')}
                    hideSearchIcon
                    valueAsCode
                    error={!!methods.formState.errors.currencyCode}
                  />
                </Box>,
              )}
              {methods.formState.errors.currencyCode && (
                <Grid
                  container
                  wrap="nowrap"
                  spacing={1}
                >
                  <Grid
                    item
                    xs={5}
                  />
                  <Grid
                    item
                    xs={7}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5,
                        color: 'var(--app-icon-critical)',
                      }}
                    >
                      <ErrorOutlineOutlinedIcon sx={{ fontSize: '0.875rem' }} />
                      <Typography variant="body_sm">
                        {methods.formState.errors.currencyCode.message}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>

            {FEE_KEYS.map((key) => (
              <Box key={key}>
                {feeRow(
                  i18n.t(`return_inbound:fee.${feeLabelKey(key)}`),
                  <RHFTextField
                    name={key}
                    type="number"
                    min={0}
                    allowDecimal={decimals > 0}
                    maxDecimals={decimals}
                    placeholder={i18n.t(`placeholder:${feeLabelKey(key)}`)}
                    startAdornment={symbol}
                    inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                  />,
                )}
              </Box>
            ))}

            <Typography variant="body_sm">{i18n.t('helper_text:return_inbound_fee')}</Typography>
          </Box>
        </FormProvider>
      }
      primaryAction={{
        content: i18n.t('common:button.confirm'),
        loading: submitting,
        disabled: submitting,
        onAction: async () => {
          const valid = await methods.trigger();
          if (!valid) return;
          setSubmitting(true);
          proceed({
            confirmed: true,
            value: {
              codFee: toFeeNumber(values.codFee, decimals),
              returnFee: toFeeNumber(values.returnFee, decimals),
              prepaidAmount: toFeeNumber(values.prepaidAmount, decimals),
              currencyCode: values.currencyCode || null,
            },
          });
        },
      }}
      onClose={() => proceed({ confirmed: false, value: null })}
    />
  );
};

export default confirmable(ReturnFeeDialog);
